'use client';

import { useTranslations } from 'next-intl';
import { type FC, type MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLineDelete } from '@/app/components/icons/BaseLineDelete';
import { BaseLineEdit } from '@/app/components/icons/BaseLineEdit';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetOverlay, SheetPortal, SheetTitle } from '@/app/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { toast } from '@/app/components/ui/use-toast';
import { useDevice, useFetch, useHoverPlayVideo, useToolContainerOverlayPosition } from '@/app/hooks';
import { cn } from '@/app/utils';
import { type MaterialV1Motion, materialsDeleteMotion, materialsListMotions, materialsUpdateMotion } from '../../apis';
import type { MotionLibraryItem } from '../types';

interface MotionLibrarySelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (item: MotionLibraryItem) => void;
}

const mapMotionToLibraryItem = (
  motion: MaterialV1Motion,
  fallbackSource: MotionLibraryItem['source'],
): MotionLibraryItem | null => {
  if (!motion.uri) {
    return null;
  }

  const source = motion.source === 'official' || motion.source === 'user' ? motion.source : fallbackSource;

  return {
    id: motion.id,
    name: motion.name || motion.id,
    thumbnail: motion.cover_uri || motion.uri,
    videoUrl: motion.uri,
    duration: motion.duration,
    source,
    createdAt: motion.created_at,
  };
};

interface MotionLibraryCardProps {
  item: MotionLibraryItem;
  isEditing: boolean;
  isUpdating: boolean;
  isDeletingId: string | null;
  editingMotionName: string;
  onSelect: (item: MotionLibraryItem) => void;
  onEdit: (event: MouseEvent<HTMLButtonElement>, item: MotionLibraryItem) => void;
  onDelete: (event: MouseEvent<HTMLButtonElement>, item: MotionLibraryItem) => void;
  onCancelEdit: (event: MouseEvent<HTMLButtonElement>) => void;
  onSaveEdit: (event: MouseEvent<HTMLButtonElement>, item: MotionLibraryItem) => void;
  onEditingNameChange: (name: string) => void;
  onUpdateMotion: (item: MotionLibraryItem, name: string) => void;
  updatingMotionId: string | null;
}

const MotionLibraryCard: FC<MotionLibraryCardProps> = ({
  item,
  isEditing,
  isUpdating,
  isDeletingId,
  editingMotionName,
  onSelect,
  onEdit,
  onDelete,
  onCancelEdit,
  onSaveEdit,
  onEditingNameChange,
  onUpdateMotion,
  updatingMotionId,
}) => {
  const t = useTranslations();
  const { videoRef, handleMouseEnter, handleMouseLeave } = useHoverPlayVideo({ resetOnLeave: true });

  const cardContent = (
    <>
      <div className="aspect-[3/4] w-full overflow-hidden bg-system-white08">
        <video
          ref={videoRef}
          src={item.videoUrl}
          className="size-full object-cover"
          muted={false}
          playsInline
          preload="none"
          poster={item.thumbnail}
        />
      </div>
      {item.duration ? (
        <div className="absolute right-1.5 bottom-1.5 rounded bg-system-black64 px-1.5 py-0.5 text-[10px] text-system-white">
          {item.duration}s
        </div>
      ) : null}
      <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-system-black80 to-transparent p-2 pt-8">
        {isEditing ? (
          <div className="space-y-1" onClick={(event) => event.stopPropagation()}>
            <Input
              value={editingMotionName}
              disabled={isUpdating}
              maxLength={30}
              onChange={(event) => onEditingNameChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  event.stopPropagation();
                  const nextName = editingMotionName.trim();
                  if (!nextName || updatingMotionId) return;

                  onUpdateMotion(item, nextName);
                }
                if (event.key === 'Escape') {
                  event.preventDefault();
                  event.stopPropagation();
                  onEditingNameChange('');
                  onCancelEdit(event as unknown as MouseEvent<HTMLButtonElement>);
                }
              }}
              className="h-7 bg-system-black64 px-2 py-1 text-system-white text-xs"
            />
            <div className="flex justify-end gap-2 text-[10px]">
              <button type="button" className="text-system-white/60" onClick={onCancelEdit}>
                {t('tools.motionControl.motionLibrary.renameCancel')}
              </button>
              <button
                type="button"
                disabled={isUpdating || !editingMotionName.trim()}
                className="text-system-white disabled:text-system-white/40"
                onClick={(event) => onSaveEdit(event, item)}
              >
                {t('tools.motionControl.motionLibrary.renameConfirm')}
              </button>
            </div>
          </div>
        ) : (
          <p className="truncate text-system-white text-xs">{item.name}</p>
        )}
      </div>
    </>
  );

  return (
    <div
      key={item.id}
      className="group relative overflow-hidden rounded-8 bg-system-white04"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isEditing ? (
        <div className="relative block w-full text-left transition-colors">{cardContent}</div>
      ) : (
        <button
          type="button"
          onClick={() => onSelect(item)}
          className="relative block w-full text-left transition-colors hover:bg-system-white08"
        >
          {cardContent}
        </button>
      )}
      {item.source === 'user' ? (
        <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
          <button
            type="button"
            disabled={isEditing || isUpdating}
            onClick={(event) => onEdit(event, item)}
            className="flex size-7 items-center justify-center rounded-full bg-system-black64 text-system-white hover:bg-system-black80 disabled:cursor-not-allowed disabled:opacity-60"
            aria-label={t('tools.motionControl.motionLibrary.rename')}
          >
            <BaseLineEdit className="size-4" />
          </button>
          <button
            type="button"
            disabled={isDeletingId === item.id || isUpdating}
            onClick={(event) => onDelete(event, item)}
            className="flex size-7 items-center justify-center rounded-full bg-system-black64 text-system-white hover:bg-system-black80 disabled:cursor-not-allowed disabled:opacity-60"
            aria-label={t('tools.motionControl.motionLibrary.delete')}
          >
            <BaseLineDelete className="size-4" />
          </button>
        </div>
      ) : null}
    </div>
  );
};

export const MotionLibrarySelectorDialog: FC<MotionLibrarySelectorDialogProps> = ({ open, onOpenChange, onSelect }) => {
  const t = useTranslations();
  const { isMobile } = useDevice();
  const [activeSource, setActiveSource] = useState<MotionLibraryItem['source']>('official');
  const [deletingMotionId, setDeletingMotionId] = useState<string | null>(null);
  const [editingMotionId, setEditingMotionId] = useState<string | null>(null);
  const [editingMotionName, setEditingMotionName] = useState('');
  const [updatingMotionId, setUpdatingMotionId] = useState<string | null>(null);
  const overlayPosition = useToolContainerOverlayPosition({
    open,
    viewportPadding: 50,
  });

  const groups = useMemo(
    () => [
      { key: 'official' as const, title: t('tools.motionControl.motionLibrary.official') },
      { key: 'user' as const, title: t('tools.motionControl.motionLibrary.mine') },
    ],
    [t],
  );

  const {
    data: motionsData,
    loading: loadingMotions,
    run: fetchMotions,
  } = useFetch(
    (source: MotionLibraryItem['source']) =>
      materialsListMotions({
        query: {
          'pager.page': 0,
          'pager.pagesz': 100,
          source,
        },
      }),
    {
      manual: true,
      onSuccess: ({ error }) => {
        if (!error) return;

        toast({
          type: 'error',
          message: error.message || t('popup.toast.networkError'),
        });
      },
    },
  );

  const { run: deleteMotion } = useFetch((id: string) => materialsDeleteMotion({ path: { id } }), {
    manual: true,
    onSuccess: ({ error }) => {
      setDeletingMotionId(null);

      if (error) {
        toast({
          type: 'error',
          message: error.message || t('tools.motionControl.motionLibrary.deleteFailed'),
        });
        return;
      }

      toast({
        type: 'success',
        message: t('tools.motionControl.motionLibrary.deleteSuccess'),
      });
      fetchMotions('user');
    },
    onError: () => {
      setDeletingMotionId(null);
      toast({
        type: 'error',
        message: t('tools.motionControl.motionLibrary.deleteFailed'),
      });
    },
  });

  const { run: updateMotion } = useFetch(
    (item: MotionLibraryItem, name: string) =>
      materialsUpdateMotion({
        path: { 'motion.id': item.id },
        body: {
          motion: {
            id: item.id,
            name,
            uri: item.videoUrl,
            cover_uri: item.thumbnail,
            duration: item.duration,
            created_at: item.createdAt || '',
          },
          mask: 'name',
        },
      }),
    {
      manual: true,
      onSuccess: ({ error }) => {
        setUpdatingMotionId(null);

        if (error) {
          toast({
            type: 'error',
            message: error.message || t('tools.motionControl.motionLibrary.renameFailed'),
          });
          return;
        }

        toast({
          type: 'success',
          message: t('tools.motionControl.motionLibrary.renameSuccess'),
        });
        setEditingMotionId(null);
        setEditingMotionName('');
        fetchMotions('user');
      },
      onError: () => {
        setUpdatingMotionId(null);
        toast({
          type: 'error',
          message: t('tools.motionControl.motionLibrary.renameFailed'),
        });
      },
    },
  );

  useEffect(() => {
    if (open) {
      fetchMotions(activeSource);
    }
  }, [activeSource, fetchMotions, open]);

  const filteredMotions = useMemo(
    () =>
      motionsData?.data?.motions
        ?.map((motion) => mapMotionToLibraryItem(motion, activeSource))
        .filter((item): item is MotionLibraryItem => !!item) || [],
    [activeSource, motionsData?.data?.motions],
  );

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSelect = useCallback(
    (item: MotionLibraryItem) => {
      onSelect(item);
      if (isMobile) {
        onOpenChange(false);
      }
    },
    [isMobile, onOpenChange, onSelect],
  );

  const handleDelete = useCallback(
    (event: MouseEvent<HTMLButtonElement>, item: MotionLibraryItem) => {
      event.stopPropagation();
      if (item.source !== 'user' || deletingMotionId) return;

      setDeletingMotionId(item.id);
      deleteMotion(item.id);
    },
    [deleteMotion, deletingMotionId],
  );

  const handleEdit = useCallback((event: MouseEvent<HTMLButtonElement>, item: MotionLibraryItem) => {
    event.stopPropagation();
    if (item.source !== 'user') return;

    setEditingMotionId(item.id);
    setEditingMotionName(item.name);
  }, []);

  const handleCancelEdit = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setEditingMotionId(null);
    setEditingMotionName('');
  }, []);

  const handleSaveEdit = useCallback(
    (event: MouseEvent<HTMLButtonElement>, item: MotionLibraryItem) => {
      event.stopPropagation();
      const nextName = editingMotionName.trim();
      if (!nextName || updatingMotionId) return;
      if (nextName === item.name) {
        setEditingMotionId(null);
        setEditingMotionName('');
        return;
      }

      setUpdatingMotionId(item.id);
      updateMotion(item, nextName);
    },
    [editingMotionName, updateMotion, updatingMotionId],
  );

  const content = (
    <div className="flex h-full min-h-0 flex-col text-system-white">
      <Tabs
        value={activeSource}
        className="flex h-full min-h-0 flex-col"
        onValueChange={(value) => {
          setEditingMotionId(null);
          setEditingMotionName('');
          setActiveSource(value as MotionLibraryItem['source']);
        }}
      >
        <TabsList className="flex h-fit w-fit gap-6 bg-transparent p-0">
          {groups.map((group) => (
            <TabsTrigger
              key={group.key}
              value={group.key}
              className={cn(
                'relative px-0 py-1 font-normal text-sm text-system-white64 hover:bg-transparent',
                activeSource === group.key &&
                  'before:-left-[1px] before:-right-[1px] font-medium text-system-white before:absolute before:bottom-0 before:h-[2px] before:rounded-full before:bg-system-white before:content-[""]',
              )}
            >
              {group.title}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="mt-4 min-h-0 flex-1 overflow-y-auto pb-4">
          {loadingMotions ? (
            <div className="grid grid-cols-4 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 4 }).map((_, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: <只是骨架屏>
                <div key={index} className="aspect-[3/4] animate-pulse rounded-8 bg-system-white08" />
              ))}
            </div>
          ) : null}
          {!loadingMotions && filteredMotions.length === 0 ? (
            <div className="flex h-full min-h-[180px] items-center justify-center text-sm text-system-white/40">
              {t('tools.motionControl.motionLibrary.empty')}
            </div>
          ) : null}
          {!loadingMotions ? (
            <div className="grid grid-cols-4 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filteredMotions.map((item) => (
                <MotionLibraryCard
                  key={item.id}
                  item={item}
                  isEditing={editingMotionId === item.id}
                  isUpdating={updatingMotionId === item.id}
                  isDeletingId={deletingMotionId}
                  editingMotionName={editingMotionName}
                  onSelect={handleSelect}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onCancelEdit={handleCancelEdit}
                  onSaveEdit={handleSaveEdit}
                  onEditingNameChange={setEditingMotionName}
                  onUpdateMotion={(motionItem, name) => {
                    setUpdatingMotionId(motionItem.id);
                    updateMotion(motionItem, name);
                  }}
                  updatingMotionId={updatingMotionId}
                />
              ))}
            </div>
          ) : null}
        </div>
      </Tabs>
    </div>
  );

  if (!open) {
    return null;
  }

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetPortal>
          <SheetOverlay>
            <SheetContent
              side="bottom"
              showClose={false}
              className="flex h-[66.6%] flex-col rounded-t-[20px] border-none bg-[#252B36] p-3"
              onPointerDownOutside={(event) => {
                event.preventDefault();
              }}
            >
              <SheetHeader className="relative h-8 flex-shrink-0 space-y-0">
                <button
                  type="button"
                  className="absolute top-0 bottom-0 left-0 h-8 px-3 text-xs leading-8"
                  onClick={handleClose}
                >
                  {t('elements.cancel')}
                </button>
                <SheetTitle className="text-center font-normal text-base leading-8">
                  {t('tools.motionControl.motionLibrary.title')}
                </SheetTitle>
              </SheetHeader>
              <div className="min-h-0 flex-1 overflow-hidden pt-3">{content}</div>
            </SheetContent>
          </SheetOverlay>
        </SheetPortal>
      </Sheet>
    );
  }

  return (
    <div
      className="fixed z-20 flex max-w-5xl flex-col overflow-hidden rounded-r-12 border-none bg-[#17191B] px-6 pt-4 pb-2 text-white"
      style={{
        top: `${overlayPosition.top}px`,
        left: `${overlayPosition.left}px`,
        height: `${overlayPosition.height}px`,
        width: `${overlayPosition.availableWidth}px`,
      }}
    >
      <Button
        size="icon"
        variant="ghost"
        className="absolute top-3 right-4 size-8 text-system-white64 hover:bg-system-white08 hover:text-system-white"
        onClick={handleClose}
      >
        <BaseLineClose className="size-4" />
      </Button>
      <div className="flex h-full min-h-0 flex-1 flex-col overflow-y-hidden pt-1">
        <div className="mb-4 font-medium text-base leading-[1.4]">{t('tools.motionControl.motionLibrary.title')}</div>
        {content}
      </div>
    </div>
  );
};
